/** t331u B9: phone Settings › Breaks & time rules (desktop WorkforceSettingsPanels TimeRules + ClockRounding). One form, one Save. */
import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { breakRuleLine, breakRuleProblem, MAX_BREAK_RULES, MISSED_CLOCK_IN_NOTE, NEW_BREAK_RULE, ROUND_MINUTE_OPTIONS, ROUND_MODE_OPTIONS, roundingExample, ROUNDING_NOTE, TIME_RULES_BREAKS_NOTE, TIME_RULES_LEGAL_NOTE, timeRulesBody, timeRulesForm, timeRulesProblems, type BreakRuleForm, type ClockRoundMode, type TimeRulesForm } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWfContext, useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Input, Select, Spinner } from '@/components/ui';
import { BottomSheet } from '../BottomSheet';
import { card, F, ghostBtn, saveBtn } from './PersonSettings';
import { listCard, note, WsAddRow, WsHead, WsRow, WsSaveBar, WsSwitch } from './WorkspaceSettings';

export function TimeRulesPage() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const wf = useWfContext();
  const settings = wf.data?.settings;
  const initial = settings ? timeRulesForm(settings) : null;
  const [f, setF] = useState<TimeRulesForm | null>(initial);
  const [rule, setRule] = useState<{ i: number | null } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (settings) setF(timeRulesForm(settings)); }, [settings]);
  if (!f || !initial) return <div className="p-4"><Spinner /></div>;
  const dirty = JSON.stringify(f) !== JSON.stringify(initial);
  const problems = timeRulesProblems(f);
  const save = async () => {
    setBusy(true);
    try { await api.wf.updateSettings(ws!, timeRulesBody(f)); toast('Time rules saved', 'success'); void queryClient.invalidateQueries({ queryKey: ['wf', ws] }); }
    catch (e) { toast(errorMessage(e, 'Couldn’t save settings'), 'error'); }
    finally { setBusy(false); }
  };
  const set = (p: Partial<TimeRulesForm>) => setF({ ...f, ...p });
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="m-time-rules">
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <WsHead>Default breaks for new shifts</WsHead>
        <div className={listCard} data-testid="m-time-rules-list">
          {!f.rules.length && <p className="border-b border-slate-100 px-3 py-3 text-sm text-slate-500 dark:border-slate-800">No default breaks: new shifts start with none.</p>}
          {f.rules.map((r, i) => { const [title, line] = breakRuleLine(r).split(' · '); return <WsRow key={i} testId="m-time-rules-row" title={title} line={line} onClick={() => setRule({ i })} />; })}
          {f.rules.length < MAX_BREAK_RULES && <WsAddRow label="Add a rule" onClick={() => setRule({ i: null })} testId="m-time-rules-add" />}
        </div>
        <p className={note}>{TIME_RULES_BREAKS_NOTE} {TIME_RULES_LEGAL_NOTE}</p>
        <WsHead>Attendance</WsHead>
        <div className={`${card} space-y-1`}>
          <F label="Late after (minutes)"><Input inputMode="numeric" value={f.grace} onChange={e => set({ grace: e.target.value })} data-testid="m-time-rules-grace" /></F>
          <F label="No-show after (minutes)"><Input inputMode="numeric" value={f.noShow} onChange={e => set({ noShow: e.target.value })} data-testid="m-time-rules-noshow" /></F>
          <F label="Auto clock-out (minutes after end)"><Input inputMode="numeric" placeholder="Off" value={f.auto} onChange={e => set({ auto: e.target.value })} data-testid="m-time-rules-auto" /></F>
        </div>
        <WsHead>Missed clock-in</WsHead>
        <div className={`${card} space-y-1`}>
          <F label="Remind them after (minutes)"><Input inputMode="numeric" placeholder="Off" value={f.nudge} onChange={e => set({ nudge: e.target.value })} data-testid="m-time-rules-nudge" /></F>
          <F label="Tell their manager after (minutes)"><Input inputMode="numeric" placeholder="Off" value={f.escalate} onChange={e => set({ escalate: e.target.value })} data-testid="m-time-rules-escalate" /></F>
        </div>
        <p className={note}>{MISSED_CLOCK_IN_NOTE}</p>
        <WsHead>Clock-time rounding</WsHead>
        <div className={`${card} space-y-1`}>
          <F label="Round to"><Select value={String(f.round)} onChange={e => set({ round: Number(e.target.value) })} data-testid="m-time-rules-round">
            {ROUND_MINUTE_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</Select></F>
          {f.round > 0 && <F label="Direction"><Select value={f.mode} onChange={e => set({ mode: e.target.value as ClockRoundMode })} data-testid="m-time-rules-mode">
            {ROUND_MODE_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</Select></F>}
          <p className="py-1.5 text-sm text-slate-700 dark:text-slate-200" data-testid="m-time-rules-example">{roundingExample(f.round, f.mode)}</p>
        </div>
        <p className={note}>{ROUNDING_NOTE}</p>
      </div>
      <WsSaveBar testId="m-time-rules" dirty={dirty} busy={busy} problems={problems} onUndo={() => setF(initial)} onSave={() => void save()} />
      {rule && <RuleSheet r={rule.i == null ? NEW_BREAK_RULE : f.rules[rule.i]} adding={rule.i == null} onClose={() => setRule(null)}
        onDone={r => set({ rules: rule.i == null ? [...f.rules, r] : f.rules.map((x, j) => (j === rule.i ? r : x)) })}
        onRemove={rule.i == null ? undefined : () => set({ rules: f.rules.filter((_, j) => j !== rule.i) })} />}
    </div>
  );
}

function RuleSheet({ r: r0, adding, onClose, onDone, onRemove }: { r: BreakRuleForm; adding: boolean; onClose: () => void; onDone: (r: BreakRuleForm) => void; onRemove?: () => void }) {
  const [r, setR] = useState(r0);
  const problem = breakRuleProblem(r);
  return (
    <BottomSheet open onClose={onClose} title={adding ? 'Add a break rule' : 'Edit break rule'} testId="m-time-rules-sheet"
      footer={<div className="flex gap-2 px-3 py-2">
        {onRemove && <button type="button" className={`${ghostBtn} text-rose-700 dark:text-rose-300`} aria-label="Remove rule" onClick={() => { onRemove(); onClose(); }} data-testid="m-time-rules-remove"><Trash2 className="h-5 w-5" /></button>}
        <button type="button" className={saveBtn} disabled={Boolean(problem)} onClick={() => { onDone(r); onClose(); }} data-testid="m-time-rules-done">{adding ? 'Add rule' : 'Done'}</button>
      </div>}>
      <div className="space-y-1 px-4 pb-2">
        <div className="grid grid-cols-2 gap-3">
          <F label="Shifts of at least (h)"><Input inputMode="decimal" value={r.hours} onChange={e => setR({ ...r, hours: e.target.value })} data-testid="m-time-rules-hours" /></F>
          <F label="Break (minutes)"><Input inputMode="numeric" value={r.minutes} onChange={e => setR({ ...r, minutes: e.target.value })} data-testid="m-time-rules-minutes" /></F>
        </div>
        <WsSwitch label="Paid" checked={r.paid} onChange={paid => setR({ ...r, paid })} testId="m-time-rules-paid" />
        {problem && <p className="text-sm text-rose-700 dark:text-rose-300" role="alert">{problem}</p>}
        <p className="pb-1 text-sm text-slate-500">Saved with the page’s Save.</p>
      </div>
    </BottomSheet>
  );
}
